import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { statsAPI } from '../api/client';
import { School, Users, Calendar, MessageSquare } from 'lucide-react';

const Dashboard = () => {
  const { user, currentOrg, isSuperAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set page title
  useEffect(() => {
    document.title = 'Ana Sayfa | İlkadım360 Hub';
  }, []);

  useEffect(() => {
    loadStats();
  }, [currentOrg]);

  const loadStats = async () => {
    try {
      if (isSuperAdmin()) {
        const response = await statsAPI.network();
        setStats(response.data);
      } else if (currentOrg) {
        const response = await statsAPI.org(currentOrg.id);
        setStats(response.data);
      }
    } catch (error) {
      console.error('Stats yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Hoş Geldiniz, {user?.fullName || `${user?.firstName} ${user?.lastName}`}!
        </h1>
        <p className="text-gray-600">
          {isSuperAdmin()
            ? 'YÖMEV Network Overview'
            : currentOrg
            ? `${currentOrg.name} - Dashboard`
            : 'Dashboard'}
        </p>
      </div>

      {isSuperAdmin() && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={School}
            label="Toplam Okul"
            value={stats.totalOrganizations}
            color="blue"
          />
          <StatCard
            icon={Users}
            label="Aktif Kullanıcı"
            value={stats.totalUsers}
            color="green"
          />
          <StatCard
            icon={Calendar}
            label="Ders Planı"
            value={stats.totalPlans}
            color="purple"
          />
          <StatCard
            icon={MessageSquare}
            label="Community Post"
            value={stats.totalPosts}
            color="orange"
          />
        </div>
      )}

      {!isSuperAdmin() && currentOrg && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <StatCard
            icon={Calendar}
            label="Ders Planları"
            value={stats.totalPlans}
            color="blue"
          />
          <StatCard
            icon={Award}
            label="Akreditasyon Kanıtları"
            value={stats.totalEvidence}
            color="green"
          />
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Hızlı Erişim</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {currentOrg && (
            <>
              <QuickLink
                to={`/org/${currentOrg.id}/planning`}
                title="Yeni Plan Oluştur"
                description="Ders planı hazırla"
              />
              <QuickLink
                to={`/org/${currentOrg.id}/accreditation`}
                title="Akreditasyon"
                description="İlerlemeyi görüntüle"
              />
            </>
          )}
          <QuickLink to="/community" title="Community" description="Paylaşım yap" />
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{label}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
};

const QuickLink = ({ to, title, description }) => {
  return (
    <a
      href={to}
      className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
    >
      <h3 className="font-medium text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </a>
  );
};

const Award = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="8" r="7" />
    <path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.12" />
  </svg>
);

export default Dashboard;
